import { css } from '@emotion/react'
import { endOfToday, format, nextSunday, previousSunday } from 'date-fns'
import Cookies from 'js-cookie'
import { observer } from 'mobx-react'
import { type FC, useEffect } from 'react'

import { tJosa } from '~/@app/i18n'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import modalService from '~/@common/services/modal.service.tsx'
import { commonRepo } from '~/@common/services/repo.service.ts'
import { colors } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import InfoTooltip from '~/@common/ui/InfoTooltip/InfoTooltip'
import LastWeeklyInfoModal from '~/@pages/home/@widgets/WeeklyInfo/LastWeeklyInfoModal.tsx'

import WeeklyInfoService from '../../@service/WeeklyInfo.service'
import SectionHeader from '../SectionHeader'
import WeeklyInfoDoubleBox from './WeeklyInfoDoubleBox'
import WeeklyInfoSingleBox from './WeeklyInfoSingleBox'

const WeeklyInfoSection: FC = () => {
  const { isMobile } = useStudentAppMediaQuery()
  const service = useRepository(WeeklyInfoService)
  const { weeklyInfo, isReady } = service
  const { studentId } = commonRepo

  const { isDailyWorksheetAllowed } = commonRepo.permission

  useEffect(() => {
    service.loadWeeklyInfo().catch(errorHandlerService.handle)
  }, [service])

  useEffect(() => {
    if (!studentId) return

    const previousSundayDate = format(previousSunday(new Date()), 'yyyy-MM-dd')

    service
      .loadLastWeeklyInfo(previousSundayDate)
      .then((lastWeeklyInfo) => {
        const cookieName = `이번주_방문_여부-${studentId}`

        if (lastWeeklyInfo.totalLearningTime >= 5 && Cookies.get(cookieName) !== 'TRUE') {
          modalService.openModal(<LastWeeklyInfoModal weeklyInfo={lastWeeklyInfo} />, {
            modalName: '지난주_학습정보',
            modalStyle: { padding: '0' },
            orderingNumber: 40,
          })
        }

        Cookies.set(cookieName, 'TRUE', { expires: nextSunday(endOfToday()) })
      })
      .catch(errorHandlerService.handle)
  }, [service, studentId])

  return (
    <section css={_Style}>
      <SectionHeader title="이번주 학습정보">
        <InfoTooltip
          title="이번주에 학습한 내용을 요약해드려요"
          items={[
            <>
              챌린지, 교재, 학습지, {tJosa('강의')} 등을 통해 학습을
              <br />
              하면 학습시간이 누적됩니다.
            </>,
            '제출한 문제 수에 따라 푼 문제 수가 누적됩니다.',
          ]}
        />
      </SectionHeader>
      {!isMobile && isDailyWorksheetAllowed ? (
        <div className="daily-learning-content-group">
          <div className="single-box-group">
            <WeeklyInfoSingleBox
              type="learningTime"
              title="총 학습시간(시간:분)"
              borderRadiusType="none"
              value={weeklyInfo?.totalLearningTimeText}
            />
            <WeeklyInfoSingleBox
              type="solvedCount"
              title="푼 문제 수"
              borderRadiusType="none"
              value={weeklyInfo?.problemsSolvedCount}
            />
          </div>
          <div className="double-box-group">
            <WeeklyInfoDoubleBox borderRadiusType="none" />
          </div>
        </div>
      ) : (
        <div className="content-group">
          <div className="single-box-group">
            <WeeklyInfoSingleBox
              type="learningTime"
              title="총 학습시간(시간:분)"
              borderRadiusType={isMobile ? 'none' : 'full'}
              value={weeklyInfo?.totalLearningTimeText}
            />
            {isMobile && isReady && <div className="devider"></div>}
            <WeeklyInfoSingleBox
              type="solvedCount"
              title="푼 문제 수"
              borderRadiusType={isMobile ? 'none' : 'full'}
              value={weeklyInfo?.problemsSolvedCount}
            />
          </div>
          <div className="weekly-info-container">
            <WeeklyInfoDoubleBox />
          </div>
        </div>
      )}
    </section>
  )
}

export default observer(WeeklyInfoSection)

const _Style = css`
  .content-group {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .single-box-group {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      height: 155px;
    }
  }

  .devider {
    width: 1px;
    height: 50px;
    background-color: ${colors.gray.$200};
  }

  .daily-learning-content-group {
    display: flex;
    flex-direction: column;
    width: 100%;
    border-radius: var(--Radius-300);
    overflow: hidden;
    .single-box-group {
      display: flex;
      height: 120px;
      width: 100%;
    }

    .double-box-group {
      height: 120px;
      border-radius: 0;
    }
  }

  .weekly-info-container {
    height: 100px;
  }

  ${mediaQuery.underTablet} {
    .content-group {
      gap: 10px;
    }
    .single-box-group {
      gap: 0;
      border-radius: var(--Radius-300);
      background: ${colors.white};
      overflow: hidden;
    }
  }
`
